import { useMemo } from 'react';
import { ChakraFlex, Text } from '@equip.health/ui';
import ProviderList from './ProviderList';
import AppointmentList, {
  ProposedAppointment,
} from './AppointmentList';
import {
  CONFIRMATION_STEP_TEST_ID,
  CONFIRMATION_SUBTITLE_TEXT,
} from '../constants';
import { useGetCareTeam } from '~/lib/hooks/care-team/useGetCareTeam';
import { INTAKE_SESSION_APPOINTMENT_TYPE_NAMES } from '~/lib/constants/scheduling.constants';
import SkeletonLoaderList from '~/components/common/SkeletonLoaderList';

export interface ConfirmationStepLegacyProps {
  appointments?: AppointmentDetailResponse[];
  isAppointmentsLoading?: boolean;
  patientId: string;
  patientTimezone?: string;
  proposedProvider: GetProviderByIdResponse;
  proposedProviderRole: string;
  proposedAppointmentSlot: ProviderAppointmentSlot | null;
  appointmentType: string;
  proposedFollowUps?: FollowUpOpening[];
  recurringAppointmentType?: string | null;
}

const ConfirmationStepLegacy = ({
  appointments,
  isAppointmentsLoading = false,
  patientId,
  patientTimezone,
  proposedProvider,
  proposedProviderRole,
  proposedAppointmentSlot,
  appointmentType,
  proposedFollowUps = [],
  recurringAppointmentType,
}: ConfirmationStepLegacyProps) => {
  const {
    providers: careTeamProviders,
    isLoading: isCareTeamLoading,
    isError: isCareTeamError,
  } = useGetCareTeam(patientId);

  const existingIntakeAppointments = useMemo(
    () =>
      (appointments ?? [])
        .filter((a) =>
          INTAKE_SESSION_APPOINTMENT_TYPE_NAMES.some((intakeType) =>
            a.appointmentTypeLabel?.startsWith(intakeType),
          ),
        )
        .toSorted(
          (a, b) =>
            new Date(a.appointmentStartDateTime).getTime() -
            new Date(b.appointmentStartDateTime).getTime(),
        ),
    [appointments],
  );

  const proposedAppointment: ProposedAppointment | null = useMemo(() => {
    if (!proposedAppointmentSlot) return null;
    return {
      startTime: proposedAppointmentSlot.startTime,
      endTime: proposedAppointmentSlot.endTime,
      appointmentType,
      provider: proposedProvider,
    };
  }, [proposedAppointmentSlot, appointmentType, proposedProvider]);

  const isLoading = isCareTeamLoading || isAppointmentsLoading;
  const isError = isCareTeamError;

  if (isLoading) {
    return (
      <ChakraFlex
        data-testid={CONFIRMATION_STEP_TEST_ID}
        gap="36px"
        height="100%"
      >
        <ChakraFlex flex="1" flexDirection="column" gap="18px">
          <SkeletonLoaderList
            direction="column"
            id="confirmation-provider-list-skeleton"
            itemCount={3}
            itemHeight="78px"
          />
        </ChakraFlex>
        <ChakraFlex flex="1" flexDirection="column" gap="18px">
          <SkeletonLoaderList
            direction="column"
            id="confirmation-appointment-list-skeleton"
            itemCount={2}
            itemHeight="78px"
          />
        </ChakraFlex>
      </ChakraFlex>
    );
  }

  if (isError) {
    return (
      <ChakraFlex
        alignItems="center"
        data-testid={CONFIRMATION_STEP_TEST_ID}
        height="100%"
        justifyContent="center"
      >
        Unable to load confirmation details. Please try again.
      </ChakraFlex>
    );
  }

  return (
    <ChakraFlex
      data-testid={CONFIRMATION_STEP_TEST_ID}
      flexDirection="column"
      gap="24px"
      height="100%"
    >
      <Text color="neutral.secondary" flexShrink={0} variant="body">
        {CONFIRMATION_SUBTITLE_TEXT}
      </Text>

      <ChakraFlex flex="1" gap="36px" minHeight={0} overflowY="auto">
        <ChakraFlex flex="1" flexDirection="column">
          <ProviderList
            existingProviders={careTeamProviders}
            proposedProvider={proposedProvider}
            proposedProviderRole={proposedProviderRole}
          />
        </ChakraFlex>
        <ChakraFlex flex="1" flexDirection="column">
          <AppointmentList
            existingAppointments={existingIntakeAppointments}
            patientTimezone={patientTimezone}
            proposedAppointment={proposedAppointment}
            proposedFollowUps={proposedFollowUps}
            recurringAppointmentType={recurringAppointmentType}
          />
        </ChakraFlex>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default ConfirmationStepLegacy;
